import BaseDrawer from '@c/drawers/BaseDrawer';
import LoadingSpinner from '@c/LoadingSpinner';
import {
  notificationFilters,
  useNotification,
} from 'context/NotificationContext';

import {
  clearAllNotifications,
  groupNotifications,
  markAllAsRead,
  NotificationList,
} from '@features/notifications';
import { cn, isMobile } from '@util/index';
import { useMemo, useState } from 'react';
import Link from 'next/link';
import { useQueryClient } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';

const NotificationDrawer = () => {
  const {
    show,
    setShow,
    notifications,
    activeFilter,
    setActiveFilter,
    isFetching,
  } = useNotification();

  const queryClient = useQueryClient();
  const [isDeletingNotifications, setIsDeletingNotifications] = useState(false);
  const groupedNotifications = groupNotifications(notifications ?? []);
  const mobile = isMobile();

  const areNotificationsEmpty = useMemo(() => {
    const isEmpty = Object.values(groupedNotifications).every(
      (notifications) => notifications.length === 0
    );
    return isEmpty;
  }, [groupedNotifications]);

  return (
    <BaseDrawer
      dismiss={() => {
        setShow(false);
      }}
      show={show}
      position="right"
      fill={mobile}
    >
      <div className="relative flex h-full w-full flex-col">
        {/* Header */}
        <div className="relative flex w-full items-center justify-between gap-4 p-[1.6rem]">
          <button
            type="button"
            className={cn(
              'whitespace-nowrap text-[1.8rem] font-semibold text-brand-secondary transition-colors duration-75 ease-linear hover:text-brand-secondary-darker',
              isDeletingNotifications && 'animate-pulse opacity-60'
            )}
            onClick={async () => {
              setIsDeletingNotifications(true);

              await clearAllNotifications();
              queryClient.invalidateQueries({
                queryKey: ['notifications'],
              });

              setIsDeletingNotifications(false);
            }}
            disabled={isDeletingNotifications}
          >
            Clear all
          </button>
          <div className="-ml-12 flex w-full justify-center">
            <span className="text-[2rem] font-semibold">Notifications</span>
          </div>
          <button
            type="button"
            onClick={() => {
              setShow(false);
            }}
            aria-label="close sheet"
            className="h-14 w-14 shrink-0 rounded-full bg-zinc-100 p-3 transition-colors duration-75 ease-linear hover:bg-zinc-200"
          >
            <XIcon className="h-full w-full text-zinc-500" />
          </button>
        </div>

        <div className="mt-8 flex w-full items-center gap-4 overflow-x-scroll px-6">
          {notificationFilters.map((filter) => (
            <button
              type="button"
              onClick={() => {
                setActiveFilter(filter);
              }}
              className={cn(
                'flex h-[4.5rem] cursor-pointer items-center justify-center rounded-full border border-zinc-200 bg-white px-10 py-2 text-[1.6rem] font-semibold capitalize text-zinc-800 transition-colors duration-75 ease-linear',
                activeFilter === filter
                  ? 'border-black bg-black text-zinc-100'
                  : 'bg-white hover:bg-zinc-100'
              )}
              key={filter}
            >
              {filter}
            </button>
          ))}
        </div>

        <div className="mt-8 flex flex-1 flex-col gap-4 overflow-hidden">
          {areNotificationsEmpty && isFetching ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner />
            </div>
          ) : (
            <NotificationList
              groupedNotifications={groupedNotifications}
              areNotificationsEmpty={areNotificationsEmpty}
              activeFilter={activeFilter}
            />
          )}
        </div>

        <div className="absolute bottom-0 z-20 w-full">
          <div className="flex w-full flex-col gap-6 px-6 py-12">
            {!areNotificationsEmpty && (
              <div className="flex w-full justify-center">
                <button
                  type="button"
                  className="flex w-fit items-center justify-center rounded-full border border-zinc-200 px-8 py-2 text-[1.8rem] font-medium text-black outline-none backdrop-blur-lg transition-all duration-75 ease-linear hover:scale-105 hover:bg-zinc-100"
                  onClick={async () => {
                    await markAllAsRead();
                    queryClient.invalidateQueries({
                      queryKey: ['notifications'],
                    });
                  }}
                >
                  Mark all as read
                </button>
              </div>
            )}

            <Link
              href={'/dashboard/notifications'}
              onClick={() => {
                setShow(false);
              }}
              className="flex h-[6rem] w-full items-center justify-center rounded-[2rem] bg-brand-secondary text-center text-[2.1rem] font-semibold text-white transition-colors duration-75 ease-linear hover:bg-brand-secondary-darker"
            >
              View all notifications
            </Link>
          </div>
        </div>

        <div className="absolute bottom-0 z-10 h-[30rem] w-full bg-gradient-to-t from-white to-transparent" />
      </div>
    </BaseDrawer>
  );
};

export { NotificationDrawer };
